// FILE: displayedUserMessage.ts
// Purpose: Turn a stored user prompt back into what the transcript should show, splitting the
//   trailing quoted-context blocks off the text the user actually typed.
// Layer: Chat transcript helpers
// Exports: the displayed-message shape, the image-only bootstrap markers, and the derivation.

import { extractTrailingQuotedContexts, type ParsedQuotedContextEntry } from "./quotedContext";

// Sent instead of an empty prompt when a user submits images with no text, so the provider has
// something to act on. It must never be shown verbatim in the transcript.
//
// Frozen wording, even though attachments are no longer images only: it is
// stored in every such message ever sent, and the transcript recognizes the
// bubble to hide by matching this exact string. A new one is added below for
// the non-image case rather than editing this, which would make every old
// message start rendering its bootstrap text.
export const IMAGE_ONLY_BOOTSTRAP_PROMPT =
  "[User attached one or more images without additional text. Respond using the conversation context and the attached image(s).]";
export const ATTACHMENT_ONLY_BOOTSTRAP_PROMPT =
  "[User attached one or more files without additional text. Respond using the conversation context and the attached file(s).]";
export const IMAGE_ONLY_VISIBLE_PLACEHOLDER = "(No Content)";

/**
 * The bootstrap prompt for a message that carries attachments and no text.
 *
 * Says "images" only when they all are — telling a model to look at the
 * attached image when what arrived was a spreadsheet is how a turn ends up
 * hunting for a picture that does not exist.
 */
export function attachmentOnlyBootstrapPrompt(
  attachments: ReadonlyArray<{ readonly type?: "image" | "file" }>,
): string {
  return attachments.length > 0 &&
    attachments.every((attachment) => (attachment.type ?? "image") === "image")
    ? IMAGE_ONLY_BOOTSTRAP_PROMPT
    : ATTACHMENT_ONLY_BOOTSTRAP_PROMPT;
}

export interface DisplayedUserMessageState {
  visibleText: string;
  copyText: string;
  contextCount: number;
  previewTitle: string | null;
  contexts: ParsedQuotedContextEntry[];
}

interface DisplayedUserMessageOptions {
  hideImageOnlyBootstrapPrompt?: boolean;
}

export function deriveDisplayedUserMessageState(
  prompt: string,
  options?: DisplayedUserMessageOptions,
): DisplayedUserMessageState {
  const extractedContexts = extractTrailingQuotedContexts(prompt);
  const trimmedPromptText = extractedContexts.promptText.trim();
  const hidePrompt =
    options?.hideImageOnlyBootstrapPrompt === true &&
    (trimmedPromptText === IMAGE_ONLY_BOOTSTRAP_PROMPT ||
      trimmedPromptText === ATTACHMENT_ONLY_BOOTSTRAP_PROMPT);
  return {
    // Keep the internal bootstrap prompt hidden while still giving image-only
    // user messages a visible bubble in the transcript.
    visibleText: hidePrompt ? IMAGE_ONLY_VISIBLE_PLACEHOLDER : extractedContexts.promptText,
    copyText: hidePrompt ? "" : prompt,
    contextCount: extractedContexts.contextCount,
    previewTitle: extractedContexts.previewTitle,
    contexts: extractedContexts.contexts,
  };
}
